import {
  Injectable,
  signal,
  Signal,
  WritableSignal,
  computed,
} from '@angular/core';
import { LogService, MetricsGridConfig, MetricsHttpService } from '@/app/core';
import type { Logger } from '@/app/core/services/log.service';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import type {
  SavedView,
  SavedViewSerializer,
  SavedViewCreatePayload,
  SavedViewPatchPayload,
  SavedViewDeserializer,
  SavedViewDeserializerResponse,
  SavedViewsResponse,
  SavedViewLifecycle,
} from './types';
import { FilterProfileService } from '@/app/filter/profiles/filter-profile.service';
import { FilterProfile } from '@/app/filter/profiles/models/filter-profile';
import { ActiveFilterService } from '@/app/core';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';

type ViewConfiguration = {
  serializer: SavedViewSerializer;
};

const LOCAL_STORAGE_MAP_KEY = 'SAVED_VIEWS';
const DEBUG = false;

export const GRID_NAME_TO_TRANSLATION = {
  REGIONS_GRID: 'main.tabs.geography.regions.title',
  DISTRICTS_GRID: 'main.tabs.geography.districts.title',
  BRANCHES_GRID: 'main.tabs.geography.branches.title',
  PRODUCT_TYPE_CATEGORIES: 'main.core.common.counts.product_types.plural',
  CUSTOMERS_GRID: 'main.tabs.customers.customers',
  RENTAL_ASSETS: 'main.tabs.equipment.rental_assets.title',
  SALESREPS_GRID: 'main.tabs.sales_reps.sales_reps',
  SALESREP_CUSTOMERS_GRID: 'main.core.common.counts.sales_reps.all',
  SALESREP_PRODUCTTYPES_GRID: 'main.core.common.counts.product_types.plural',
  RENTALASSETS_GRID: 'main.tabs.equipment.rental_assets.title',
  PRODUCT_TYPE_SALESREPS_GRID: 'main.core.common.counts.product_types.plural',
  PRODUCT_TYPE_CUSTOMERS_GRID: 'main.tabs.customers.customers',
  PRODUCT_TYPE_RENTALASSETS_GRID: 'main.tabs.equipment.rental_assets.title',
};

/**
 * When a grid initialized we will register the grid name, grid config factory and serializer
 * with the saved views service.  When the user interacts with the saved modals the grid name will
 * be taken from this interaction.
 */

interface ISavedViewsService {
  isLoaded: Signal<boolean>;
  isDefaultViewSet: Signal<boolean>;
  currentGridName: Signal<string>;
  views: Signal<Map<string, SavedView>>;
  awaitingAcceptView: Signal<string | null>;
  defaultViewName: Signal<string>;
  isDirty: Signal<boolean>;
  lifecycle: Signal<SavedViewLifecycle>;
  isApplying: Signal<boolean>;
  previouslySelectedViewsStack: Signal<string[]>;
  setDefaultView(): void;
  setSelectedView(gridName: string, viewName: string): Promise<string | null>;
  notifyViewApplied(): void;
  load(): Promise<SavedView[]>;
  patch(): Promise<SavedView>;
  delete(savedView: SavedView): Promise<void>;
  saveAs(data: { reportName: string; setAsDefault: boolean }): Promise<TODO>;
  initGrid<T>(gridName: string, serializer: () => TODO): void;
  handleSavedViewChange<T>(
    view: SavedView,
    currentConfig: T,
    reducer: SavedViewDeserializer<T>,
    getVisibleColumns?: (config: T) => string[]
  ): Promise<SavedViewDeserializerResponse<T>>;
  getSerializedViewName(viewName: string): string;
  deserializeViewName(serializedName: string): string;
}

@Injectable({
  providedIn: 'root',
})
export class SavedViewsService implements ISavedViewsService {
  private _previouslySelectedViewsStack: WritableSignal<string[]> = signal([]);
  private _saveAsCopy = signal(null);
  private _isDefaultViewSet: WritableSignal<boolean> = signal(false);
  public readonly isDefaultViewSet: Signal<boolean> =
    this._isDefaultViewSet.asReadonly();
  private _isLoaded: WritableSignal<boolean> = signal(false);
  public readonly isLoaded: Signal<boolean> = this._isLoaded.asReadonly();
  private _isDirty: WritableSignal<boolean> = signal(false);
  public readonly isDirty: Signal<boolean> = this._isDirty.asReadonly();
  protected configurations: Map<string, ViewConfiguration> = new Map();

  // Lifecycle status - used by page components to avoid race conditions
  private _lifecycle: WritableSignal<SavedViewLifecycle> = signal('idle');
  public readonly lifecycle: Signal<SavedViewLifecycle> =
    this._lifecycle.asReadonly();

  /**
   * Convenience computed: true when the service is in the process of applying a view
   * (either grid config or filters). Page components should NOT run configureGrid/load
   * when this is true — the saved view effect will handle it.
   */
  public readonly isApplying: Signal<boolean> = computed(() => {
    const status = this._lifecycle();
    return status === 'applying-view' || status === 'applying-filters';
  });

  // SavedViews state
  public currentGridName: WritableSignal<string> = signal('');
  public views: WritableSignal<Map<string, SavedView>> = signal(new Map());
  public defaultViewName: WritableSignal<string> = signal('');
  protected selectedViewName: WritableSignal<string> = signal('');

  public previouslySelectedViewsStack: Signal<string[]> =
    this._previouslySelectedViewsStack.asReadonly();

  /**
   * Get all available views as an array for UI components (dropdowns, etc.)
   */
  public allViewsList = computed(() => {
    return Array.from(this.views().values());
  });

  public currentGridViews = computed(() => {
    const grid = this.currentGridName();
    const keys = this.views().keys();
    const views = new Map();
    for (const key of keys) {
      const view = this.views().get(key);
      if (view.grid_id === grid) {
        views.set(key, view);
      }
    }
    return views;
  });

  /**
   *This is a list of views for the current grid.
   */
  public currentGridViewsList = computed(() => {
    const grid = this.currentGridName();
    const views = this.allViewsList().filter((view) => {
      if (view.grid_id === grid) {
        return view;
      }
    });
    return views;
  });

  // Accept view confirmation state.
  public awaitingAcceptView: WritableSignal<string | null> = signal(null);
  protected awaitingAcceptViewResolve;
  protected awaitingAcceptViewReject;
  private filterChangeSubscription: Subscription | null = null;
  public isConfirmFiltersDialogOpen: Signal<boolean> = computed(() => {
    return this.awaitingAcceptView() !== null;
  });

  public isManagementDialogOpen: WritableSignal<boolean> = signal(false);

  constructor(
    private metricsApiService: MetricsHttpService,
    private filterProfileService: FilterProfileService,
    private activeFilterService: ActiveFilterService,
    private featureFlagService: FeatureFlagService,
    private logService: LogService
  ) {
    this.logger = this.logService.getModuleLogger(
      'SavedViews',
      'SavedViewsService',
      false
    );
  }

  private logger: Logger;

  async initGrid<T>(
    gridName: string,
    serializer: SavedViewSerializer
  ): Promise<void> {
    this._lifecycle.set('idle');
    this.awaitingAcceptView.set(null);
    this.markDirty(false);
    this.currentGridName.set(gridName);
    this._isLoaded.set(false);
    this._previouslySelectedViewsStack.set([]);
    if (!this.configurations.has(gridName)) {
      this.configurations.set(gridName, { serializer });
    }
  }

  public reset(): void {
    // Clean up any existing subscription
    if (this.filterChangeSubscription) {
      this.filterChangeSubscription.unsubscribe();
      this.filterChangeSubscription = null;
    }
    this._lifecycle.set('idle');
    this._isLoaded.set(false);
    this._isDefaultViewSet.set(false);
    this.selectedViewName.set('');
    this.currentGridName.set('');
    this.defaultViewName.set('');
  }

  private processViewResponse(result: SavedViewsResponse): void {
    const views = new Map<string, SavedView>();
    result.saved_views.forEach((view: SavedView) => {
      if (
        view.grid_id === this.currentGridName() &&
        view.parent_saved_view_id === 'default'
      ) {
        this.defaultViewName.set(view.name);
      }
      // This property comes back from API, causes failures when in update requests.
      if ((view as any).permissions_web !== undefined) {
        delete (view as any).permissions_web;
      }
      views.set(view.name, view);
    });
    this.views.set(views);
  }

  async load(): Promise<any> {
    this.logger.info('load  called for grid: ', this.currentGridName());
    this._lifecycle.set('loading-views');
    this._isLoaded.set(false);

    const url = '/customer-profiles/saved-views/';
    let result: any;
    // Implementation for loading saved views
    try {
      result = await this.metricsApiService.get(url).toPromise();
    } catch (error) {
      this.logger.error('Error loading saved views:', error);
      this._isLoaded.set(true);
      this._lifecycle.set('ready');
      return;
    } finally {
      this._isLoaded.set(true);
    }

    this.processViewResponse(result);

    this.logger.info(
      'load  loaded views:\n',
      this.views(),
      '\n defaultView:',
      this.defaultViewName,
      '\n selectedView:',
      this.selectedViewName()
    );
    // Log all SavedViews and their visible columns
    const allViews = this.views();
    if (DEBUG) {
      if (allViews && allViews.size > 0) {
        Array.from(allViews.values()).forEach((view, index) => {
          this.logger.info(`\n${index + 1}. SavedView: "${view.name}"`);
          this.logger.info('   Visible Columns:');
          if (view.columns && view.columns.length > 0) {
            view.columns.forEach((col, colIndex) => {
              this.logger.info(`      ${colIndex + 1}. ${col}`);
            });
          } else {
            this.logger.info('      (no columns)');
          }
        });
      }
    }
    // Set to 'ready' after views are loaded (if no view will be applied next, this stays)
    this._lifecycle.set('ready');
    return this.views();
  }

  /**
   * We had an issue where a SavedView somehow had no "saved_view_id", which broke the delete process.
   * This enforce algo adds an id if it's missing and the DB replaces it with a correct id.
   */
  public enforceSavedViewIds(list: SavedView[]): SavedView[] {
    return list.map((item: SavedView) => this.enforceSavedViewId(item));
  }

  private createSyntheticSavedViewId(): string {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }
    const values = new Uint32Array(4);
    globalThis.crypto.getRandomValues(values);
    return Array.from(values, (value) =>
      value.toString(16).padStart(8, '0')
    ).join('');
  }

  private enforceSavedViewId(item: SavedView): SavedView {
    if (item.saved_view_id) {
      return { ...item };
    }
    return {
      ...item,
      saved_view_id: this.createSyntheticSavedViewId(),
    };
  }

  async saveAs(data: {
    reportName: string;
    setAsDefault: boolean;
  }): Promise<TODO> {
    this.logger.info('saveAs called with:', data);

    if (!this.validateViewName(data.reportName)) {
      // TODO: Translate
      return Promise.reject(
        new Error(
          `A saved view with the name "${data.reportName}" already exists. Please choose a different name.`
        )
      );
    }

    const serializer = this.configurations.get(
      this.currentGridName()
    )?.serializer;
    if (!serializer) {
      // TODO: Translate
      return Promise.reject(
        new Error(`No serializer found for grid: ${this.currentGridName()}`)
      );
    }

    // if the new view is being set as default, we need to find any
    // existing default views for this grid and clear their default status.
    if (data.setAsDefault) {
      const allViews = this.allViewsList();
      let found = 0;
      const updates = allViews.map((view) => {
        if (
          view.parent_saved_view_id === 'default' &&
          view.grid_id === this.currentGridName()
        ) {
          found += 1;
        }
        view.parent_saved_view_id = '';

        return this.enforceSavedViewId(view);
      });
      if (found > 0) {
        try {
          await this.put({ saved_views: updates });
        } catch (error) {
          this.logger.error('Error updating existing default views:', error);
        }
      }
    }
    const profileId = await this.filterProfileService.getFilterProfileId();
    if (DEBUG) {
      this.logger.info('Current filter profile id:', profileId);
    }

    const payload: SavedViewCreatePayload = serializer();
    payload.grid_id = this.currentGridName();
    payload.name = data.reportName;
    payload.saved_view_id = data.setAsDefault ? 'default' : '';
    payload.filter_profile_id = `${profileId}`; // this has to be a string or request will fail.

    // "frozen_columns" is required by the API, but will never be used.
    if (payload.frozen_columns === undefined) {
      payload.frozen_columns = [];
    }

    const url = '/customer-profiles/saved-views/';
    /**
     * {
     *     "name": "A New Default",
     *     "grid_id": "Equipment",
     *     "saved_view_id": "default",
     *     "columns": [
     *         "eq_number",
     *         "list_price"
     *     ],
     *     "sort": [
     *         {
     *             "key": "eq_number",
     *             "direction": "desc",
     *             "priority": 1
     *         }
     *     ],
     *     "frozen_columns": []
     * }
     */

    try {
      const response = await this.metricsApiService
        .post(url, payload)
        .toPromise();
      this.markDirty(false);

      await this.load();
      await new Promise((resolve) => setTimeout(resolve, 250));

      // Directly set the selected view without going through awaitingAcceptView
      // since Save As creates a view that matches the current state - no confirmation needed
      this.selectedViewName.set(response.name);
      this.updateStorage();
      // If this was set as default, update the flag
      if (data.setAsDefault) {
        this._isDefaultViewSet.set(true);
      }
      if (DEBUG) {
        this.logger.info(
          '[SAVE AS COMPLETE] Created and selected new view:',
          response.name
        );
      }

      return response;
    } catch (error) {
      this.logger.error('Error saving new view:', error);
      throw error;
    }
  }

  async put(data?: TODO, reload = true): Promise<TODO> {
    const url = `/customer-profiles/saved-views/`;

    try {
      const result = await this.metricsApiService
        .put(url, data)
        .toPromise()
        .then((updatedView) => {
          this.markDirty(false);
          if (reload) {
            this.load();
          }
          return updatedView;
        });
      this.processViewResponse(result as SavedViewsResponse);
      return result;
    } catch (error) {
      this.logger.error('Error updating view:', error);
    }
  }

  async patch(data?: TODO, metaDataOnly = false, reload = true): Promise<TODO> {
    const url = `/customer-profiles/saved-views/`;
    const currentSavedView = data || this.getSelectedView();
    let serializer: SavedViewSerializer;
    if (!metaDataOnly) {
      serializer = this.configurations.get(this.currentGridName())?.serializer;

      if (!serializer) {
        // TODO: Translate
        return Promise.reject(
          new Error(`No serializer found for grid: ${this.currentGridName()}`)
        );
      }
    }

    const profileId = await this.filterProfileService.getFilterProfileId();
    if (DEBUG) {
      this.logger.info('Current filter profile id:', profileId);
    }

    const payload: SavedViewPatchPayload = serializer ? serializer() : {};
    payload.name = currentSavedView.name;
    if (!metaDataOnly) {
      payload.filter_profile_id = `${profileId}`;
    } else {
      payload.filter_profile_id = currentSavedView.filter_profile_id;
    }
    payload.saved_view_id = currentSavedView.saved_view_id;
    payload.parent_saved_view_id = currentSavedView.parent_saved_view_id;
    // payload.last_selected_id = null;

    // "frozen_columns" is required by the API, but will never be used.
    if (payload.frozen_columns === undefined) {
      payload.frozen_columns = [];
    }

    return this.metricsApiService
      .patch(url, payload)
      .toPromise()
      .then((updatedView) => {
        this.markDirty(false);
        if (reload) {
          this.load();
        }
        return updatedView;
      });
  }

  async delete(savedView: SavedView, reload = true): Promise<void> {
    const url = `/customer-profiles/saved-views/`;

    const payload = {
      saved_view_id: savedView.saved_view_id,
    };

    try {
      // Delete the saved view first
      await this.metricsApiService.delete(url, payload).toPromise();
      this.logger.info('Deleted saved view:', savedView.saved_view_id);

      // Check if we need to delete the associated filter profile
      const filterProfileId =
        typeof savedView.filter_profile_id === 'string'
          ? parseInt(savedView.filter_profile_id as string, 10)
          : savedView.filter_profile_id;

      if (filterProfileId) {
        // Find the filter profile to check its name
        const filterProfile =
          this.filterProfileService.profileMap?.userProfiles.find(
            (profile: FilterProfile) => profile.profileId === filterProfileId
          );

        if (filterProfile && filterProfile.profileName.startsWith('SV--')) {
          this.logger.info(
            'Deleting associated filter profile:',
            filterProfileId,
            filterProfile.profileName
          );
          try {
            await this.filterProfileService.deleteProfileById(filterProfileId);
            this.logger.info(
              'Successfully deleted filter profile:',
              filterProfileId
            );
          } catch (profileError) {
            this.logger.error(
              'Error deleting filter profile:',
              filterProfileId,
              profileError
            );
            // Don't throw here - the saved view was already deleted successfully
          }
        } else {
          this.logger.info(
            'Filter profile does not start with SV--, skipping deletion:',
            filterProfile?.profileName
          );
        }
      }

      if (reload) {
        await this.load();
      }
    } catch (error) {
      this.logger.error('Error deleting saved view:', error);
      throw error;
    }
  }

  public async setDefaultView() {
    if (DEBUG) {
      this.logger.info(
        'saved-views.service.setDefaultView  called',
        this.defaultViewName()
      );
    }
    const result = await this.setSelectedView(
      this.currentGridName(),
      this.defaultViewName()
    );
    // Only mark as set if the view was actually applied (not cancelled)
    if (result !== null) {
      this._isDefaultViewSet.set(true);
    }
  }

  /**
   * Sets the selected view by name. This will trigger any effects watching selectedView.
   */
  public setSelectedView(
    gridName: string,
    viewName: string
  ): Promise<string | null> {
    this.logger.info(
      'setSelectedView  called with gridName:',
      gridName,
      'viewName:',
      viewName
    );

    // Signal that we're starting to apply a view
    this._lifecycle.set('applying-view');

    // Remove the session grid settings if there is a SavedView selected.
    if (viewName !== null) {
      const sessionKey = `GRID_CFG_${gridName}`;
      sessionStorage.removeItem(sessionKey);
    }

    // Create a promise to track the user's response to the confirmation dialog if filters will change.
    // This promise will be resolved when the user accepts the new view and the filters are applied,
    // or resolved with null if the user cancels.
    // IMPORTANT: If a previous setSelectedView call is still pending, resolve it with null first
    // to prevent the previous caller from hanging forever (e.g., beginLoad's await).
    if (this.awaitingAcceptViewResolve) {
      this.awaitingAcceptViewResolve(null);
    }
    const prom = new Promise<string | null>((resolve, reject) => {
      this.awaitingAcceptViewResolve = resolve;
      this.awaitingAcceptViewReject = reject;
    });

    if (this.currentGridName() !== gridName) {
      this.currentGridName.set(gridName);
    }

    // If viewName is null, it means we want to clear the current view selection
    // (e.g. when switching grids or resetting to default).
    if (viewName === null) {
      this.awaitingAcceptView.set(null);
      this.awaitingAcceptViewResolve(null);
      this._lifecycle.set('ready');
      return prom;
    }

    const views = this.views();
    if (views.has(viewName)) {
      const viewToApply = views.get(viewName);

      // Check if this is the initial default view being set on page load
      const isInitialDefaultViewLoad = !this._isDefaultViewSet();

      // Check if filters would actually change
      const wouldFiltersChange =
        this.wouldApplyingSavedViewChangeFilters(viewToApply);

      if (!wouldFiltersChange) {
        // Filters won't change — apply view directly without confirmation

        // Mark that the default view has been set on initial load
        if (isInitialDefaultViewLoad) {
          this._isDefaultViewSet.set(true);
        }

        // Check if the view has a filter profile that needs to be applied
        const filterProfileId = viewToApply.filter_profile_id
          ? typeof viewToApply.filter_profile_id === 'string'
            ? parseInt(viewToApply.filter_profile_id as string, 10)
            : viewToApply.filter_profile_id
          : null;

        // On initial load with a filter profile, apply it FIRST to ensure filter state is correct,
        // then set selectedViewName so the component effect fires with correct filters.
        if (isInitialDefaultViewLoad && filterProfileId) {
          this.logger.info(
            '[SET-VIEW] Initial load - applying filter profile BEFORE grid config',
            { wouldFiltersChange, filterProfileId }
          );
          this._lifecycle.set('applying-filters');
          this.applyFilterProfileFromView(viewToApply).then(() => {
            // Now that filters are applied, set the selected view name.
            // This triggers the component effect which will apply grid config + load().
            this._lifecycle.set('applying-view');
            this.updatePreviouslySelectedViewsStack();
            this.selectedViewName.set(viewName);
            this.updateStorage();
            this.awaitingAcceptViewResolve(viewName);
          });
        } else {
          this.logger.info(
            '[SET-VIEW] Skipping filter profile application - filters already match or no profile',
            { wouldFiltersChange, filterProfileId, isInitialDefaultViewLoad }
          );
          // Filters already match or no filter profile — safe to set selectedViewName immediately
          this.updatePreviouslySelectedViewsStack();
          this.selectedViewName.set(viewName);
          this.updateStorage();
          this._lifecycle.set('ready');
          setTimeout(() => {
            this.awaitingAcceptViewResolve(viewName);
          });
        }

        return prom;
      } else {
        // Filters will change - show confirmation dialog
        this.logger.info(
          'setSelectedView: Filters WILL change, requiring confirmation',
          {
            wouldFiltersChange,
            isInitialDefaultViewLoad,
          }
        );

        // Mark that default view has been set even when showing dialog
        if (isInitialDefaultViewLoad) {
          this._isDefaultViewSet.set(true);
        }

        // Stay in 'applying-view' — will transition when user accepts or cancels
        this.awaitingAcceptView.set(viewName);
      }
    } else {
      const error = `View "${viewName}" not found in loaded views`;
      this.logger.warn(error, this.views());
      this.awaitingAcceptView.set(null);
      this._lifecycle.set('ready');
      setTimeout(() => {
        this.awaitingAcceptViewResolve(null);
      });
    }
    return prom;
  }

  public getSelectedView(): SavedView | null {
    const isActive = this.featureFlagService.isActive('flagSavedViews');
    if (!isActive) {
      return null;
    }
    const viewName = this.selectedViewName();
    const views = this.views();
    if (views.has(viewName)) {
      return views.get(viewName);
    }
    return null;
  }

  /**
   * Applies the filter profile from a saved view without showing the confirmation dialog.
   * Used when we're certain filters won't change or can't determine but still need to apply the profile.
   */
  private applyFilterProfileFromView(view: SavedView): Promise<void> {
    return new Promise<void>((resolve) => {
      const filterProfileId =
        typeof view.filter_profile_id === 'string'
          ? parseInt(view.filter_profile_id as string, 10)
          : view.filter_profile_id;

      if (filterProfileId) {
        if (this.filterChangeSubscription) {
          this.filterChangeSubscription.unsubscribe();
          this.filterChangeSubscription = null;
        }

        // Use take(1) to ensure the subscription automatically completes after the first emission
        this.filterChangeSubscription = this.activeFilterService.filterChange
          .pipe(take(1))
          .subscribe(() => {
            // Clean up the subscription after it fires
            this.filterChangeSubscription = null;
            resolve();
          });

        this.filterProfileService.requestProfileId(filterProfileId);
      } else {
        // No filter profile to apply - resolve immediately
        resolve();
      }
    });
  }

  private updatePreviouslySelectedViewsStack(): void {
    const currentViewName = this.selectedViewName();
    this._previouslySelectedViewsStack.set([
      currentViewName,
      ...this.previouslySelectedViewsStack(),
    ]);
  }

  public acceptNewView(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.awaitingAcceptView() !== null) {
        const viewName = this.awaitingAcceptView();
        this.awaitingAcceptView.set(null);

        // Get the view to determine if we need to apply filters
        const views = this.views();
        const view = views.get(viewName);
        this.logger.info('[ACCEPT] Accepted view: ', viewName, view);

        const filterProfileId = view
          ? typeof view.filter_profile_id === 'string'
            ? parseInt(view.filter_profile_id as string, 10)
            : view.filter_profile_id
          : null;

        if (filterProfileId) {
          // CRITICAL FIX: Clean up any existing subscription before creating a new one
          if (this.filterChangeSubscription) {
            this.filterChangeSubscription.unsubscribe();
            this.filterChangeSubscription = null;
          }

          // Apply filters FIRST, then set selectedViewName so the component effect
          // fires with the correct filter state already in place.
          this._lifecycle.set('applying-filters');

          // Use take(1) to ensure the subscription automatically completes after the first emission
          this.filterChangeSubscription = this.activeFilterService.filterChange
            .pipe(take(1))
            .subscribe(() => {
              // Clean up the subscription after it fires
              this.filterChangeSubscription = null;

              // Now that filters are applied, set the selected view name.
              // This triggers the component effect which applies grid config + load().
              this._lifecycle.set('applying-view');
              this.updatePreviouslySelectedViewsStack();
              this.selectedViewName.set(viewName);
              this.updateStorage();
              this.awaitingAcceptViewResolve(this.selectedViewName());
              resolve();
            });

          this.filterProfileService.requestProfileId(filterProfileId);
        } else {
          // No filter profile to apply — set selectedViewName immediately
          this.updatePreviouslySelectedViewsStack();
          this.selectedViewName.set(viewName);
          this.updateStorage();
          this.awaitingAcceptViewResolve(this.selectedViewName());
          this._lifecycle.set('ready');
          resolve();
        }
      } else {
        resolve();
      }
    });
  }

  public cancelNewView(): void {
    // Clean up any existing subscription
    if (this.filterChangeSubscription) {
      this.filterChangeSubscription.unsubscribe();
      this.filterChangeSubscription = null;
    }
    this.awaitingAcceptView.set(null);
    // Resolve with null to indicate cancellation (not an error).
    // Using reject here causes Zone.js to report an unhandled rejection
    // through Angular's ErrorHandler before the awaiting catch can process it.
    if (this.awaitingAcceptViewResolve) {
      this.awaitingAcceptViewResolve(null);
    }
    this._lifecycle.set('ready');
  }

  private updateStorage() {
    const data =
      JSON.parse(sessionStorage.getItem(LOCAL_STORAGE_MAP_KEY)) || {};
    data[this.currentGridName()] = this.selectedViewName();
    sessionStorage.setItem(LOCAL_STORAGE_MAP_KEY, JSON.stringify(data));
  }

  /**
   * Called by page components after the saved view grid config has been applied and load() called.
   * Transitions lifecycle to 'ready', signaling that the full application cycle is complete.
   */
  public notifyViewApplied(): void {
    this.logger.info('[LIFECYCLE] View fully applied, transitioning to ready');
    this._lifecycle.set('ready');
  }

  /**
   * Gets the current selected view name as a signal
   */
  public getSelectedViewName = this.selectedViewName.asReadonly();

  private validateViewName(name: string): boolean {
    const views = this.currentGridViews();
    const key = `${this.currentGridName()}__${name}`;
    return !views.has(key);
  }

  markDirty(isDirty: boolean = true): void {
    this.logger.info('markDirty  marking view as dirty:', isDirty);
    this._isDirty.set(isDirty);
  }

  // The api our service uses disallows reports that have the same name.
  // Because I think that users will actually want to use the same name across different grids,
  // I'm prepending the grid name to the view name for storage and retrieval in the service.
  // This allows users to have a "Revenue by Product" view in both the Products and Customers grids
  // without conflict.  The getSerializedViewName and deserializeViewName methods handle this transformation.
  public getSerializedViewName(name: string): string {
    return `${this.currentGridName()}__${name}`;
  }

  public deserializeViewName(serializedName: string): string {
    if (!serializedName) {
      return '';
    }
    const prefix = `${this.currentGridName()}__`;
    if (serializedName.startsWith(prefix)) {
      return serializedName.substring(prefix.length);
    }
    return serializedName; // fallback to original if it doesn't match expected format
  }

  public getDefaultView() {
    const storage =
      JSON.parse(window.sessionStorage.getItem(LOCAL_STORAGE_MAP_KEY)) || {};
    let viewName: string;
    if (storage[this.currentGridName()]) {
      viewName = storage[this.currentGridName()];
      if (DEBUG) {
        this.logger.info(
          'getDefaultView found view name in storage for grid ',
          this.currentGridName(),
          ': ',
          viewName
        );
      }
    } else {
      if (DEBUG) {
        this.logger.info(
          'getDefaultView did not find view name in storage for grid ',
          this.currentGridName(),
          ' using defaultViewName: ',
          this.defaultViewName()
        );
      }

      viewName = this.defaultViewName();
    }
    const view = this.views().get(viewName);
    return view || null;
  }

  /**
   * Determines if applying the given SavedView's filter profile would change the currently applied filters.
   * @param savedView The SavedView to check
   * @returns true if applying the SavedView would change filters, false if the filters are the same
   */
  private wouldApplyingSavedViewChangeFilters(savedView: SavedView): boolean {
    // Get the target filterProfileId from the SavedView
    const targetFilterProfileId =
      typeof savedView.filter_profile_id === 'string'
        ? parseInt(savedView.filter_profile_id as string, 10)
        : savedView.filter_profile_id;

    if (DEBUG) {
      this.logger.info('🔍 wouldApplyingSavedViewChangeFilters START', {
        savedViewName: savedView.name,
        savedViewFilterProfileId: savedView.filter_profile_id,
        targetFilterProfileId,
      });
    }

    // Get the current filter profile ID
    const currentFilterProfile = this.filterProfileService.currentProfile.value;
    const currentFilterProfileId = currentFilterProfile?.profileId;

    if (DEBUG) {
      this.logger.info(
        '🔍 wouldApplyingSavedViewChangeFilters: Comparing profile IDs',
        {
          currentProfileId: currentFilterProfileId,
          currentProfileName: currentFilterProfile?.profileName,
          targetProfileId: targetFilterProfileId,
        }
      );
    }

    // If there's no target filter profile ID, it uses the company default
    // We need to compare against current filters
    if (!targetFilterProfileId) {
      // If current also has no profile, filters are the same
      if (!currentFilterProfileId) {
        if (DEBUG) {
          this.logger.info(
            '🔍 wouldApplyingSavedViewChangeFilters: Both profiles are null/undefined - returning false'
          );
        }
        return false;
      }
      // Current has a profile but target doesn't - need to compare values
      // Fall through to deep comparison below
      if (DEBUG) {
        this.logger.info(
          '🔍 wouldApplyingSavedViewChangeFilters: Target has no profile, current has one - will do deep comparison'
        );
      }
    }

    // Find the target profile to do deep comparison
    // NOTE: We always do deep comparison because profile IDs can differ even when filter values are identical
    // (e.g., SV-- profiles get new IDs on each save but may have the same filter values)
    // First check userProfiles, then check clientProfile (which has profileId = -1)
    let targetProfile: FilterProfile | null = null;
    if (targetFilterProfileId) {
      targetProfile =
        this.filterProfileService.profileMap?.userProfiles.find(
          (profile: FilterProfile) =>
            profile.profileId === targetFilterProfileId
        ) ?? null;

      // If not found in userProfiles and target is -1 (system default), use clientProfile
      if (!targetProfile && targetFilterProfileId === -1) {
        targetProfile =
          this.filterProfileService.profileMap?.clientProfile ?? null;
      }

      if (DEBUG) {
        this.logger.info(
          '🔍 wouldApplyingSavedViewChangeFilters: Target profile lookup',
          {
            targetFilterProfileId,
            foundProfile: !!targetProfile,
            foundProfileName: targetProfile?.profileName,
            availableUserProfiles:
              this.filterProfileService.profileMap?.userProfiles?.map((p) => ({
                id: p.profileId,
                name: p.profileName,
              })),
            clientProfileId:
              this.filterProfileService.profileMap?.clientProfile?.profileId,
          }
        );
      }
    }

    if (targetFilterProfileId && !targetProfile) {
      if (DEBUG) {
        this.logger.warn(
          '🔍 wouldApplyingSavedViewChangeFilters: Target profile not found in profileMap - returning TRUE (assuming change)',
          {
            targetFilterProfileId,
            availableProfiles:
              this.filterProfileService.profileMap?.userProfiles?.map(
                (p) => p.profileId
              ),
          }
        );
      }

      // Can't find the target profile - assume filters would change to be safe
      return true;
    }

    // Convert the target profile to FilterValues (or use default if no profile)
    const targetFilterValues = targetProfile
      ? FilterProfile.asFilterValues(targetProfile)
      : this.activeFilterService.getCurrentFilter(); // Use current as baseline if no target profile

    // Get the current filter values from ActiveFilterService
    const currentFilterValues = this.activeFilterService.getCurrentFilter();

    if (DEBUG) {
      this.logger.info(
        '🔍 wouldApplyingSavedViewChangeFilters: Filter values for comparison',
        {
          currentFilterValues,
          targetFilterValues,
        }
      );
    }

    // Get the list of fields to compare from FilterProfile model
    // Filter out 'months' and 'monthsCode' as they don't exist on FilterValues
    // (FilterValues uses monthFromId, monthToId, dateFrom, dateTo instead)
    const fieldsToCompare = FilterProfile.getComparableFilterFields().filter(
      (field) => field !== 'months' && field !== 'monthsCode'
    );

    if (DEBUG) {
      this.logger.info(
        '🔍 wouldApplyingSavedViewChangeFilters: Fields to compare',
        {
          fieldsToCompare,
          originalFields: FilterProfile.getComparableFilterFields(),
        }
      );
    }

    // Add date comparison fields (but we'll compare them specially)
    const dateFields = ['dateFrom', 'dateTo'];
    const monthIdFields = ['monthFromId', 'monthToId'];

    // Track all differences for debugging
    const diffs: { field: string; current: any; target: any; type: string }[] =
      [];

    // Compare non-date fields using isEqual
    for (const field of fieldsToCompare) {
      const currentVal = currentFilterValues[field];
      const targetVal = targetFilterValues[field];
      const areEqual = isEqual(currentVal, targetVal);

      if (!areEqual) {
        diffs.push({
          field,
          current: currentVal,
          target: targetVal,
          type: 'regular',
        });
        if (DEBUG) {
          this.logger.warn(`🔍 DIFF FOUND - Field "${field}":`, {
            current: currentVal,
            target: targetVal,
            currentType: typeof currentVal,
            targetType: typeof targetVal,
            currentIsArray: Array.isArray(currentVal),
            targetIsArray: Array.isArray(targetVal),
            currentStringified: JSON.stringify(currentVal),
            targetStringified: JSON.stringify(targetVal),
          });
        }
      }
    }

    // Compare month ID fields (these are more reliable than Date objects)
    for (const field of monthIdFields) {
      const currentVal = currentFilterValues[field];
      const targetVal = targetFilterValues[field];
      const areEqual = currentVal === targetVal;

      if (!areEqual) {
        diffs.push({
          field,
          current: currentVal,
          target: targetVal,
          type: 'monthId',
        });
        if (DEBUG) {
          this.logger.warn(`🔍 DIFF FOUND - Month field "${field}":`, {
            current: currentVal,
            target: targetVal,
          });
        }
      }
    }

    // Compare date fields by their date portion only (ignoring timezone differences)
    // This handles the case where one date is GMT (e.g., "2026-05-01T00:00:00Z")
    // and another is PST (e.g., "2026-05-01T00:00:00-07:00") but represent the same calendar date
    for (const field of dateFields) {
      const currentDate = currentFilterValues[field];
      const targetDate = targetFilterValues[field];

      // Helper to extract just the date portion (YYYY-MM-DD) from a date
      const getDateString = (date: any): string | null => {
        if (!date) {
          return null;
        }
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) {
          return null;
        }
        // Use local date components to get the calendar date
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const currentDateStr = getDateString(currentDate);
      const targetDateStr = getDateString(targetDate);
      const areEqual = currentDateStr === targetDateStr;

      if (!areEqual) {
        diffs.push({
          field,
          current: currentDate,
          target: targetDate,
          type: 'date',
        });
        if (DEBUG) {
          this.logger.warn(`🔍 DIFF FOUND - Date field "${field}":`, {
            currentDate,
            targetDate,
            currentDateStr,
            targetDateStr,
            currentIsDate: currentDate instanceof Date,
            targetIsDate: targetDate instanceof Date,
          });
        }
      }
    }

    // Log final result with summary
    if (diffs.length > 0) {
      if (DEBUG) {
        this.logger.warn(
          `🔍 wouldApplyingSavedViewChangeFilters RESULT: TRUE (${diffs.length} difference(s) found)`,
          {
            savedViewName: savedView.name,
            differencesCount: diffs.length,
            differences: diffs,
            firstDiff: diffs[0],
          }
        );
      }
      return true;
    }

    if (DEBUG) {
      this.logger.info(
        '🔍 wouldApplyingSavedViewChangeFilters RESULT: FALSE (All filters match)',
        { savedViewName: savedView.name }
      );
    }
    return false; // Filters are the same
  }

  /**
   * Handle saved view changes by applying the view to a grid configuration
   * @param view - The SavedView to apply
   * @param currentConfig - The current grid configuration
   * @param reducer - A function that applies the view to the config and returns the new config
   * @param getVisibleColumns - Optional function to extract visible column names from config for logging
   * @returns Promise<T> - The new configuration after applying the view
   */
  public async handleSavedViewChange<T>(
    view: SavedView,
    currentConfig: T,
    deserializer: SavedViewDeserializer<T>,
    getVisibleColumns?: (config: T) => string[]
  ): Promise<SavedViewDeserializerResponse<T>> {
    if (DEBUG) {
      this.logger.info(
        `++++++++++++++++ handleSavedViewChange ${view.name} ++++++++++++++++++`
      );
    }

    // view.columns.forEach((col) => this.logger.info('   -', col));

    // Log current visible columns if callback provided
    if (getVisibleColumns) {
      const currentVisible = getVisibleColumns(currentConfig);
      currentVisible.forEach((title) => this.logger.info('   -', title));
    }

    // Clone the current config to avoid mutations
    const clonedConfig = cloneDeep(currentConfig);

    // Apply the saved view to the current config using the provided reducer
    const response = await deserializer(clonedConfig, view);

    // Log new visible columns if callback provided
    if (getVisibleColumns) {
      const newVisible = getVisibleColumns(response.config);
      newVisible.forEach((title) => this.logger.info('   -', title));
    }

    return response;
  }
}
